import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useState } from 'react';
import VotingSessionController from './controllers/VotingSessionController';
import type VotingSessionWidget from '../../../widgets/VotingSessionWidget';
import styles from './VotingSession.module.css';
import classNames from 'classnames';
import { durations, shortFormattedTime } from '../../../utils/time';
import { pluralize } from '@aha-app/aha-shared';
import customElementBoolAttr from '../../../utils/customElementBoolAttr';
import FistToFiveDetails from './FistToFiveDetails';
import PokerDetails from './PokerDetails';
import { VotingSessionType } from '../../../widgets/VotingSessionType';

const userIsFacilitator = (
  widget: VotingSessionWidget,
  controller: VotingSessionController
): boolean => {
  return widget.belongsToUser(controller.userId);
};

const Details: React.FC<{ widget: VotingSessionWidget }> = ({ widget }) => {
  const controller = useController(VotingSessionController);
  const [endingSession, setEndingSession] = useState<boolean>(false);
  const { remainingTime } = controller.state;
  const { votesRemaining, finishedVoting } = controller;
  const remainingTimeString = shortFormattedTime(
    controller.state.remainingTime
  );
  const remainingTimeFactor = durations[remainingTimeString.unit].factor;

  const finishedVotingCount = Object.values(controller.voters).filter(
    Boolean
  ).length;
  const voterCount = Object.keys(controller.voters).length;

  const sessionType = widget.sessionType;
  const isFacilitator = userIsFacilitator(widget, controller);

  // Planning poker results can be quite tall so we need to make space for them
  const isShowingPokerResults =
    sessionType === VotingSessionType.Poker &&
    controller.state.widget?.currentVotingObject?.votingStatus === 'finished';

  const isPokerObjectSelection =
    sessionType === VotingSessionType.Poker &&
    !controller.state.widget?.currentVotingObject;

  return (
    <div className={classNames('flex-col', styles.height100Percent)}>
      <div className='flex-row flex-col--align-stretch'>
        {remainingTime < Infinity && (
          <div
            className={classNames(
              'flex-col flex-col--align-center gap-6',
              styles.votingDetailStatus
            )}
          >
            <i className='fa-regular fa-timer color-secondary t-050' />
            <p className='m-0'>
              <span className='h-300'>{remainingTimeString.string}</span>
              <span className='t-100 color-secondary'>
                {' '}
                {remainingTimeString.shortUnit}
              </span>
            </p>
            <div
              className={classNames(
                'flex-row gap-2',
                styles.votingAddTimeButtons
              )}
            >
              <aha-button
                size='small'
                onClick={() =>
                  controller.actionExtendVotingSession(remainingTimeFactor)
                }
              >
                <i className='fa-regular fa-plus' />
                <span>
                  1&nbsp;{durations[remainingTimeString.unit].shortName}
                </span>
              </aha-button>
              <aha-button
                size='small'
                onClick={() =>
                  controller.actionExtendVotingSession(5 * remainingTimeFactor)
                }
              >
                <i className='fa-regular fa-plus' />
                <span>
                  5&nbsp;
                  {pluralize(5, durations[remainingTimeString.unit].shortName)}
                </span>
              </aha-button>
            </div>
          </div>
        )}
        {!isShowingPokerResults && !isPokerObjectSelection && (
          <div
            className={classNames(
              'flex-col flex-col--align-center gap-6',
              styles.votingDetailStatus
            )}
          >
            <i className='fa-regular fa-users color-secondary t-050' />
            <p className='m-0 text-center'>
              <span className='h-300'>{finishedVotingCount}</span>
              <span className='t-100 color-secondary'> of </span>
              <span className='h-300'>{voterCount}</span>
              <span className='t-100 color-secondary'> voted</span>
            </p>
          </div>
        )}
      </div>
      {widget.votesPerPerson && (
        <div
          className={classNames(styles.votingRoundPill, {
            [styles.votingRoundPillGray]: !finishedVoting,
            [styles.votingRoundPillGreen]: finishedVoting,
          })}
        >
          <div className='flex-row flex-row--align-center gap-1 t-100'>
            {finishedVoting ? (
              <span>
                You have successfully voted
                <i className='fa-regular fa-check pl-1' />
              </span>
            ) : (
              <>
                <span>You have</span>
                <span className='h-500 font-500 color-primary'>
                  {votesRemaining}
                </span>
                <span>{pluralize(votesRemaining, 'vote')} left</span>
              </>
            )}
          </div>
        </div>
      )}
      <div className={classNames('flex-col', 'gap-2', styles.height100Percent)}>
        {sessionType === VotingSessionType.FistToFive && (
          <FistToFiveDetails widget={widget} />
        )}
        {sessionType === VotingSessionType.Poker && (
          <PokerDetails widget={widget} isFacilitator={isFacilitator} />
        )}
        {sessionType === VotingSessionType.Standard && (
          <div>
            <aha-button
              kind='primary'
              disabled={customElementBoolAttr(finishedVoting)}
              size='large'
              style={{ width: '100%' }}
              onClick={() => {
                controller.actionFinishVoting();
              }}
            >
              <i className='fa-regular fa-check mr-2' /> I am done
            </aha-button>
          </div>
        )}
        <div
          hidden={
            endingSession ||
            (sessionType === VotingSessionType.Poker && !isFacilitator)
          }
        >
          <aha-button
            kind={
              sessionType === VotingSessionType.Poker ? 'gray' : 'secondary'
            }
            size='large'
            style={{ width: '100%' }}
            onClick={() => {
              setEndingSession(true);
            }}
          >
            End voting session
          </aha-button>
        </div>
        <div
          className={classNames(styles.votingEndConfirmation, 'p-3')}
          hidden={!endingSession}
        >
          <p className='h-700'>
            <strong>End the session</strong>
          </p>
          <p className='mb-6'>
            This will end the voting session for all participants.
          </p>
          <div className={styles.votingConfirmActions}>
            <aha-button
              kind='secondary'
              onClick={() => setEndingSession(false)}
            >
              Cancel
            </aha-button>
            <aha-button
              kind='primary'
              class={styles.votingGrowButton}
              onClick={() => {
                controller.actionStopVotingSession();
                setEndingSession(false);
              }}
            >
              End now
            </aha-button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationView(Details);
